import ActionCable from 'actioncable';
import { isPublicPath } from './publicPaths';
import { buildLoginRedirectUrl } from '../utils/authReturnTo';

// Reject-storm guard: if the server closes the WebSocket within
// IMMEDIATE_CLOSE_MS of opening it REJECT_LIMIT times in a row, stop the
// monitor and force a full re-auth via /login. Without this, a tab whose
// JWT has expired or whose jwt_token cookie is missing reconnects forever —
// JavaScript can't see httpOnly cookies, and it can't distinguish "expired
// session" from "legit network blip" without timing the close.
const REJECT_LIMIT = 3;
const IMMEDIATE_CLOSE_MS = 2000;

// Fast reconnect after a server-initiated disconnect (dyno restart/deploy).
// The server sends {type: "disconnect", reconnect: true} right before it
// closes the socket. This 5.2 client has no handler for that message: it only
// notices the close, then waits for the monitor's stale threshold (6 s) plus
// its poll backoff (3-30 s) before reopening. Measured on preview
// 2026-09-30: 10-33 s without live updates on every dyno restart, 58 s
// before the server-side fix. Reopen right after the close instead; the
// small jitter keeps a fleet of tabs from reconnecting in lockstep.
const FAST_RECONNECT_MIN_MS = 250;
const FAST_RECONNECT_JITTER_MS = 750;
// Hard ceiling on the fast path: at most one immediate reopen per window.
// A second server-announced close inside the window falls back to the
// monitor's normal backoff, so nothing a server does can turn this into a
// tight retry loop.
const FAST_RECONNECT_WINDOW_MS = 10000;

const ActionCableJwt = {
  createConnection: () => {
    ActionCable.getConfig = () => null;
    ActionCable.createWebSocketURL = url => url.replace(/^http/, 'ws');

    // Auth rides the WebSocket upgrade handshake via the httpOnly `jwt_token` cookie —
    // no JWT subprotocol injection required anymore.

    // Guard against undefined entries during reconnect (prevents transient HMR errors).
    // Unrelated to auth — preserved from the pre-cookie implementation.
    if (ActionCable?.Subscriptions && !ActionCable.Subscriptions.prototype._notifyPatched) {
      ActionCable.Subscriptions.prototype.notify = function (subOrId, callbackName, ...args) {
        const list = typeof subOrId === 'string' ? this.findAll(subOrId) : [subOrId];
        const safe = list.filter(Boolean);
        return safe.map(s => (typeof s?.[callbackName] === 'function' ? s[callbackName](...args) : undefined));
      };
      ActionCable.Subscriptions.prototype._notifyPatched = true;
    }

    // See REJECT_LIMIT / IMMEDIATE_CLOSE_MS comment above.
    // Patched on Connection.prototype.events so every consumer picks it up
    // via the default installEventHandlers path.
    if (ActionCable?.Connection?.prototype && !ActionCable.Connection.prototype._rejectGuardPatched) {
      const origEvents = ActionCable.Connection.prototype.events || {};

      ActionCable.Connection.prototype.events = {
        ...origEvents,

        message(event) {
          // See FAST_RECONNECT_* above. Remember that the server asked us to
          // come back, then let the socket close normally; `close` below does
          // the reopen. Anything else goes to the stock handler untouched.
          try {
            const data = JSON.parse(event.data);
            if (data && data.type === 'disconnect') {
              this._serverAskedReconnect = data.reconnect !== false;
              return;
            }
          } catch (_) { /* not JSON, stock handler decides */ }
          if (typeof origEvents.message === 'function') {
            return origEvents.message.call(this, event);
          }
        },

        open(event) {
          this._lastOpenAt = Date.now();
          this._serverAskedReconnect = false;
          if (typeof origEvents.open === 'function') {
            return origEvents.open.call(this, event);
          }
        },

        close(event) {
          const serverAskedReconnect = this._serverAskedReconnect === true;
          this._serverAskedReconnect = false;

          // A close the server announced with reconnect: true is a restart,
          // not a rejection: run the stock close (records the disconnect,
          // notifies subscriptions) and reopen almost immediately. It must not
          // count toward the reject guard, or a deploy could log users out.
          if (serverAskedReconnect) {
            this._lastOpenAt = 0;
            if (typeof origEvents.close === 'function') {
              origEvents.close.call(this, event);
            }
            const now = Date.now();
            if (this._lastFastReopenAt && now - this._lastFastReopenAt < FAST_RECONNECT_WINDOW_MS) {
              return; // second restart inside the window: let the monitor's backoff handle it
            }
            this._lastFastReopenAt = now;
            const delay = FAST_RECONNECT_MIN_MS + Math.random() * FAST_RECONNECT_JITTER_MS;
            setTimeout(() => {
              if (!this.isActive() && this.monitor && this.monitor.isRunning()) {
                this.open();
              }
            }, delay);
            return;
          }

          // If 'open' never fired (pure network error, DNS failure, router 5xx
          // before upgrade), let the monitor retry normally. Timing-based
          // reject detection only makes sense after a successful upgrade.
          if (!this._lastOpenAt) {
            if (typeof origEvents.close === 'function') {
              return origEvents.close.call(this, event);
            }
            return;
          }

          const uptime = Date.now() - this._lastOpenAt;
          this._lastOpenAt = 0;

          if (uptime < IMMEDIATE_CLOSE_MS) {
            this._rejectCount = (this._rejectCount || 0) + 1;
            if (this._rejectCount >= REJECT_LIMIT) {
              try { this.monitor && this.monitor.stop && this.monitor.stop(); } catch (_) { /* no-op */ }
              if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
                window.location.href = buildLoginRedirectUrl(window.location);
              }
              return; // skip origEvents.close — it would record a disconnect and reschedule reconnect
            }
          } else {
            this._rejectCount = 0;
          }

          if (typeof origEvents.close === 'function') {
            return origEvents.close.call(this, event);
          }
        },
      };

      ActionCable.Connection.prototype._rejectGuardPatched = true;
    }

    return ActionCable;
  },
};

export default ActionCableJwt;
